/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { usageCancelStripeSubscription } from "../funcs/usageCancelStripeSubscription.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { StripeSubscription } from "../models/components/stripesubscription.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CancelStripeSubscriptionRequest,
  CancelStripeSubscriptionSecurity,
} from "../models/operations/cancelstripesubscription.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CancelStripeSubscriptionMutationVariables = {
  request?: CancelStripeSubscriptionRequest | undefined;
  security?: CancelStripeSubscriptionSecurity | undefined;
  options?: RequestOptions;
};

export type CancelStripeSubscriptionMutationData = StripeSubscription;

export type CancelStripeSubscriptionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * cancelStripeSubscription usage
 *
 * @remarks
 * Schedule the organization's Stripe PAYG subscription to cancel at the end of its current period
 */
export function useCancelStripeSubscriptionMutation(
  options?: MutationHookOptions<
    CancelStripeSubscriptionMutationData,
    CancelStripeSubscriptionMutationError,
    CancelStripeSubscriptionMutationVariables
  >,
): UseMutationResult<
  CancelStripeSubscriptionMutationData,
  CancelStripeSubscriptionMutationError,
  CancelStripeSubscriptionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCancelStripeSubscriptionMutation(client, options),
    ...options,
  });
}

export function mutationKeyCancelStripeSubscription(): MutationKey {
  return ["@gram/client", "usage", "cancelStripeSubscription"];
}

export function buildCancelStripeSubscriptionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CancelStripeSubscriptionMutationVariables,
  ) => Promise<CancelStripeSubscriptionMutationData>;
} {
  return {
    mutationKey: mutationKeyCancelStripeSubscription(),
    mutationFn: function cancelStripeSubscriptionMutationFn({
      request,
      security,
      options,
    }): Promise<CancelStripeSubscriptionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(usageCancelStripeSubscription(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
